import React from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, TrendingUpIcon, StarIcon } from 'lucide-react';
interface SpotlightLocation {
  id: number;
  name: string;
  tagline: string;
  priceRange: string;
  bestFor: string;
  insight: string;
  trend: string;
  image: string;
}
export function CompletedDeals() {
  const locations: SpotlightLocation[] = [{
    id: 1,
    name: 'Marina Golden Zone',
    tagline: "Cabo's High-Velocity Rental Engine",
    priceRange: '$250K – $900K',
    bestFor: 'Cash Flow',
    insight: 'Highest year-round occupancy in Cabo. Walk-to-everything location drives premium nightly rates and consistent bookings.',
    trend: 'Strong & Steady',
    image: 'https://images.unsplash.com/photo-1519046904884-53103b34b206?q=80&w=1600&auto=format&fit=crop'
  }, {
    id: 2,
    name: 'Pedregal',
    tagline: 'The Beverly Hills of Cabo',
    priceRange: '$1.5M – $15M+',
    bestFor: 'Luxury Equity',
    insight: 'Gated, hillside, ocean-view estates. Limited inventory and zero new land create durable price appreciation at the top of the market.',
    trend: 'Premium Appreciation',
    image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?q=80&w=1600&auto=format&fit=crop'
  }, {
    id: 3,
    name: 'The Corridor',
    tagline: 'Resort-Branded Investment Spine',
    priceRange: '$500K – $5M',
    bestFor: 'Branded Residences',
    insight: 'The 20-mile stretch connecting Cabo San Lucas and San José. Home to most of the major branded developments — Four Seasons, Ritz, Montage.',
    trend: 'Active Development',
    image: 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?q=80&w=1600&auto=format&fit=crop'
  }, {
    id: 4,
    name: 'East Cape',
    tagline: "Cabo's Next Frontier",
    priceRange: '$200K – $3M',
    bestFor: 'Land Banking',
    insight: 'Costa Palmas, Four Seasons, and Aman are anchoring what was undeveloped coastline a decade ago. Early positioning still possible.',
    trend: 'Emerging Upside',
    image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=1600&auto=format&fit=crop'
  }, {
    id: 5,
    name: 'Pacific Side',
    tagline: 'Quiet Coast, Big View',
    priceRange: '$400K – $4M',
    bestFor: 'Long-Term Hold',
    insight: 'Diamante, Quivira, and surrounding communities. Less density, more privacy, dramatic Pacific exposure — a different buyer profile entirely.',
    trend: 'Steady Growth',
    image: 'https://images.unsplash.com/photo-1505881502353-a1986add3762?q=80&w=1600&auto=format&fit=crop'
  }, {
    id: 6,
    name: 'San José del Cabo',
    tagline: 'The Cultural Counterweight',
    priceRange: '$300K – $2M',
    bestFor: 'Lifestyle + Yield',
    insight: 'Art district, walkable historic core, and a maturing dining scene. Increasingly attractive to buyers seeking authenticity over resort polish.',
    trend: 'Rising Demand',
    image: 'https://images.unsplash.com/photo-1518684079-3c830dcef090?q=80&w=1600&auto=format&fit=crop'
  }];
  return <section id="spotlight" className="py-24 bg-white" data-id="element-41" data-mp="8mrrnrbsebrk">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-42" data-mp="dka6fychjl7h">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-43" data-mp="djqpajunt0ui">
          <div className="flex items-center justify-center mb-4" data-id="element-44" data-mp="3q26zl55827be">
            <StarIcon className="text-gold mr-2" size={28} data-id="element-45" data-mp="3pzlxcf10hpd4" />
            <span className="text-gold text-sm font-semibold tracking-wider uppercase" data-id="element-46" data-mp="119kxup7y438y">
              Trevor's Picks
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-serif font-bold text-navy mb-6" data-id="element-47" data-mp="13uajcciasg30">
            Spotlight Locations
          </h2>
          <p className="text-xl text-slate max-w-3xl mx-auto" data-id="element-48" data-mp="ywirtjlkevzc">
            Where the smart capital is going. A curated look at the areas of
            Cabo worth understanding — and why each one matters.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-49" data-mp="18vq7wejop4q5">
          {locations.map((location, index) => <motion.div key={location.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="group bg-white rounded-lg overflow-hidden border border-gray-200 hover:border-gold hover:shadow-xl transition-all" data-id="element-50" data-mp="1hubj1omtthgw" data-mpi={index}>
              {/* Image */}
              <div className="relative h-48 overflow-hidden" data-id="element-51" data-mp="2p5fug44wxwue">
                <img src={location.image} alt={location.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-52" data-mp="2p3ll7n307t65" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" data-id="element-53" data-mp="21gdgb69hmm4n" />
                <div className="absolute top-4 left-4" data-id="element-54" data-mp="266twgy4p0lup">
                  <span className="bg-gold text-navy px-3 py-1 rounded text-xs font-bold uppercase tracking-wider" data-id="element-55" data-mp="268enidrstmnt">
                    {location.bestFor}
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 flex items-center text-white" data-id="element-56" data-mp="1whk4di9un36l">
                  <MapPinIcon size={18} className="mr-2" data-id="element-57" data-mp="1iikfqamc6oaf" />
                  <span className="text-sm font-medium" data-id="element-58" data-mp="1ng2l5r15aldo">Cabo San Lucas</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6" data-id="element-59" data-mp="3p72sp7gs276e">
                <h3 className="text-2xl font-serif font-bold text-navy mb-1" data-id="element-60" data-mp="3keta6chzs9e5">
                  {location.name}
                </h3>
                <p className="text-gold text-sm font-semibold italic mb-4" data-id="element-61" data-mp="3gcemnf3dolgj">
                  {location.tagline}
                </p>

                <p className="text-slate leading-relaxed mb-5 text-sm" data-id="element-62" data-mp="3bfy0gqfqoh63">
                  {location.insight}
                </p>

                <div className="space-y-2 pt-4 border-t border-gray-200" data-id="element-63" data-mp="7xehyfmv4poh">
                  <div className="flex justify-between items-center" data-id="element-64" data-mp="7va5h9aweicl">
                    <span className="text-slate text-xs uppercase tracking-wider" data-id="element-65" data-mp="2x08qu9iz9ov">
                      Typical Range
                    </span>
                    <span className="text-navy font-bold font-mono text-sm" data-id="element-66" data-mp="2noivepwkvlr5">
                      {location.priceRange}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-67" data-mp="2io4kn03ldxpf">
                    <span className="text-slate text-xs uppercase tracking-wider" data-id="element-68" data-mp="28mk3hx9sb9b4">
                      Market Trend
                    </span>
                    <div className="flex items-center" data-id="element-69" data-mp="2di984f0nvgea">
                      <TrendingUpIcon size={14} className="text-gold mr-1" data-id="element-70" data-mp="2iineztqj0kaq" />
                      <span className="text-navy font-semibold text-sm" data-id="element-71" data-mp="2nax63d3dnqel">
                        {location.trend}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="text-center mt-12" data-id="element-72" data-mp="31a6gtx8tdsb6">
          <p className="text-slate italic" data-id="element-73" data-mp="1bw9r2hbuexra">
            Track record and verified deal performance coming soon as our
            portfolio of completed transactions grows.
          </p>
        </motion.div>
      </div>
    </section>;
}